import { LocationType } from 'api-order-client'
import { head, prop, sortBy } from 'ramda'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { setSelectedContextAction } from '~shared-components/redux/actions/login.actions'
import {
  loadSelectedContextState,
  loadSelectedSalesGroupId,
  saveSelectedContextState,
} from '~shared-components/redux/localStorage'
import {
  ApplicationUser,
  SalesGroupWithCustomers,
} from '~shared-components/transforms/user.transforms'

import { useSelectSalesGroup } from './useSelectSalesGroup'

interface Parameters {
  isEnabled?: boolean
  user: ApplicationUser | undefined
}

export const useInitLocale = (parameters: Parameters) => {
  const { isEnabled = true, user } = parameters

  const dispatch = useDispatch()
  const { setSelectSalesGroup } = useSelectSalesGroup()

  useEffect(() => {
    let selectedSalesGroup: SalesGroupWithCustomers | undefined

    if (!user || !isEnabled) return

    if (user.salesGroups && user.salesGroups.length > 0) {
      // PART 1 - init the selected sales ggroup from storage
      const salesGroupIdFromStorage = loadSelectedSalesGroupId(user.email)
      const salesGroupId =
        user.salesGroups.find(g => g.id === salesGroupIdFromStorage)?.id ||
        user.salesGroups[0].id
      selectedSalesGroup = user.salesGroups.find(
        group => group.id === salesGroupId,
      )

      if (selectedSalesGroup) {
        setSelectSalesGroup(selectedSalesGroup)
      }
    }

    //PART 2 - init the context from storage
    if (selectedSalesGroup && user.customers && user.customers.length > 0) {
      const savedContext = loadSelectedContextState(user.email)
      const storedContext = user.customers.find(
        c =>
          c.customerContextId ===
            savedContext?.currentContext.selectedContextId &&
          c.salesGroup.id === selectedSalesGroup!.id,
      )
      const defaultContext =
        user.customers.find(c => c.salesGroup.id === selectedSalesGroup!.id) ??
        user.customers[0]

      const storedLocationId =
        storedContext?.locations.find(
          ({ id, type }) =>
            type === LocationType.ShipTo &&
            id === savedContext?.currentContext.selectedLocationId,
        )?.id ?? ''
      const defaultLocationId =
        head(
          sortBy(
            prop('name'),
            defaultContext.locations.filter(
              location => location.type === LocationType.ShipTo,
            ),
          ),
        )?.id ?? ''

      const selectedContext = storedContext || defaultContext
      const selectedLocationId =
        selectedContext === defaultContext
          ? defaultLocationId
          : storedLocationId

      saveSelectedContextState(user.email, {
        selectedContextId: selectedContext.customerContextId,
        selectedLocationId,
      })

      dispatch(
        setSelectedContextAction({
          context: selectedContext,
          locationId: selectedLocationId,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])
}
